import React, { useState, useEffect, useCallback, useMemo } from "react";
import { login, getAuthToken } from "./auth";
import NetworkIndicatorIcon from "../assets/icons/network-indicator-icon.svg";
import LLMIndicatorIcon from "../assets/icons/llm-indicator-icon.svg";
import VoiceIndicatorIcon from "../assets/icons/voice-indicator-icon.svg";
import axios from "axios";

export default function ConnectionTest({ passphrase, configId, micOn }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [apiConnected, setApiConnected] = useState(false);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [authToken, setAuthToken] = useState("");
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const CHECK_INTERVAL = 60000;

  // Check Internet Connection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [setIsOnline]);

  // Check API Connection
  useEffect(() => {
    const performLogin = async () => {
      try {
        const token = await login();
        setAuthToken(token);
      } catch (error) {
        console.error("Failed to login:", error);
      }
    };

    performLogin();
  }, [authToken]);

  const checkApiStatus = useCallback(async () => {
    try {
      const token = getAuthToken();
      await axios.post(
        `${BASE_URL}/api/chat?passphrase=${passphrase}&config_id=${configId}&app_id=1`, // endpoint for checking
        {
          prompt: "health-check",
          use_audio: "false",
          use_expression: "false",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setApiConnected(true);
    } catch (error) {
      console.error("API is not available:", error);
      setApiConnected(false);
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(checkApiStatus, CHECK_INTERVAL);
    checkApiStatus();
    return () => clearInterval(interval);
  }, [checkApiStatus]);

  // Check Voice Connection
  useEffect(() => {
    const checkConnection = async () => {
      const openaiAPIKey = process.env.REACT_APP_OPENAI_API_KEY;

      try {
        const response = await fetch(
          "https://api.openai.com/v1/audio/transcriptions",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${openaiAPIKey}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.ok) {
          setVoiceConnected(true);
          console.log("Connection successful!");
        } else {
          setVoiceConnected(true);
          //   console.error("Connection failed. Status:", response.status);
        }
      } catch (error) {
        setVoiceConnected(false);
        console.error("Error:", error);
      }
    };

    checkConnection();
  }, []);

  const netStyle = useMemo(() => {
    if (isOnline) {
      return { backgroundColor: "green" };
    } else {
      return { backgroundColor: "red" };
    }
  }, [isOnline]);

  const llmStyle = useMemo(() => {
    return {
      backgroundColor: isOnline && apiConnected ? "green" : "red",
    };
  }, [isOnline, apiConnected]);

  const voiceStyle = useMemo(() => {
    if (micOn !== undefined) {
      return { backgroundColor: micOn ? "green" : "red" };
    }
    return {
      backgroundColor: isOnline && voiceConnected ? "green" : "red",
    };
  }, [isOnline, voiceConnected, micOn]);

  return (
    <div className="app__indicator">
      <div className="network-indicator indicator-item">
        <img src={NetworkIndicatorIcon} alt="" />
        <div className="indicator" style={netStyle}></div>
      </div>
      <div className="llm-indicator indicator-item">
        <img src={LLMIndicatorIcon} alt="" />
        <div className="indicator" style={llmStyle}></div>
      </div>
      <div className="voice-indicator indicator-item">
        <img src={VoiceIndicatorIcon} alt="" />
        <div className="indicator" style={voiceStyle}></div>
      </div>
    </div>
  );
}
